import type { ContentInterface } from '../amplienceTypes/content';
import type { GenericLayoutSlot } from '../amplienceTypes/schemas/imported/generic-layout-slot-schema';
import type { MasterLayout as MasterLayoutSchema } from '../amplienceTypes/schemas/imported/master-layout-schema';
import type { MasterLayoutSlot } from '../amplienceTypes/schemas/imported/master-layout-slot-schema';

import dynamic from 'next/dynamic';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { MasterLayout } from '../components/shared/core/layouts/MasterLayout';
import { PDPMetaHead } from '../components/shared/metaHead/PDPMetaHead';
import { Placeholder } from '../components/shared/placeholder/Placeholder';
import { Breadcrumbs } from '~/components/pdp/productDetail/productDetailControl/Breadcrumbs';
import { ProductScrollHandler } from '~/components/pdp/productScrollHandler/ProductScrollHandler';
import { showLayoutContent } from '~/utilities/amplience';
import { PageTypes } from '~/utilities/constants';
import { ExperienceOptionsProvider } from '~/utilities/context/dynamic/ExperienceOptionsProvider';
import { GalleryContextProvider } from '~/utilities/context/dynamic/GalleryContext';
import { LayoutDesignContextProvider } from '~/utilities/context/dynamic/LayoutDesignContext';
import { PDPDynamicContextProvider } from '~/utilities/context/dynamic/PDPDynamicContext';
import { useDataLayerContext } from '~/utilities/context/static/DataLayerContext';
import { PDPContextProvider } from '~/utilities/context/static/PDPContext';
import { useStaticContext } from '~/utilities/context/static/StaticContext';
import type { CategoryDetails, Configuration, Product } from '~/utilities/graphql/codegen';
import { getProductDataLayerEvent } from '~/utilities/vendors';

const ClearPreviewModeCookiesButton = dynamic(
  async () =>
    (await import('~/components/shared/cookie/ClearPreviewModeCookiesButton'))
      .ClearPreviewModeCookiesButton
);

const S = {
  Wrapper: styled.div`
    & > div:first-child > div:first-child {
      padding-bottom: ${({ theme }) => theme.padding.lg};
    }
  `,
};

interface PDPViewProps {
  productData?: Product;
  genericLayoutSlotContent: (ContentInterface & Pick<GenericLayoutSlot, 'pageProperties'>) | null;
  masterLayoutSlotContent: (MasterLayoutSlot & { slotContent: MasterLayoutSchema }) | null;
  configuration: Configuration;
  isPreviewMode: boolean;
  productStructuredData: string | null;
  categoryDetails?: CategoryDetails;
}

export const PDPView = ({
  productData,
  genericLayoutSlotContent,
  masterLayoutSlotContent,
  isPreviewMode,
  productStructuredData,
  categoryDetails,
}: PDPViewProps) => {
  const {
    configuration: { enablePDPBreadcrumbs },
  } = useStaticContext();
  const { pushToDataLayer } = useDataLayerContext();
  const [pageLoaded, setPageLoaded] = useState(false);

  useEffect(() => {
    if (productData && !pageLoaded) {
      const eecProductDetailViewEvent = getProductDataLayerEvent(
        'eecProductDetailView',
        productData
      );

      if (eecProductDetailViewEvent) {
        pushToDataLayer(eecProductDetailViewEvent);
        setPageLoaded(true);
      }
    }
  }, [pageLoaded, productData, pushToDataLayer]);
  // Sends product data upon request
  useEffect(() => {
    const sendPdpProductData = () => {
      document.dispatchEvent(
        new CustomEvent('pdp-product-data-response', {
          detail: {
            ...productData,
          },
        })
      );
    };

    sendPdpProductData();
    document.addEventListener('pdp-product-data-request', sendPdpProductData);

    return () => document.removeEventListener('pdp-product-data-request', sendPdpProductData);
  }, [productData]);

  const layoutSlots =
    genericLayoutSlotContent?.slotContent?.layout?.layoutSlot.map(slot => slot?.slots) || [];
  const layoutComponents = layoutSlots[0]?.children;

  const layoutContent = useMemo(() => showLayoutContent(layoutComponents), [layoutComponents]);

  const main = layoutContent.length !== 0 ? layoutContent : <Placeholder />;

  const metadataContent = genericLayoutSlotContent?.metaData;
  const uspListContent = genericLayoutSlotContent?.uspList;

  return (
    <PDPContextProvider product={productData ?? {}} categoryDetails={categoryDetails}>
      <PDPDynamicContextProvider code={productData?.code}>
        <LayoutDesignContextProvider layoutComponents={layoutComponents || []}>
          <GalleryContextProvider>
            <PDPMetaHead
              productStructuredData={productStructuredData}
              metadataContent={metadataContent}
            />

            {isPreviewMode && <ClearPreviewModeCookiesButton />}
            <ExperienceOptionsProvider>
              <MasterLayout
                pageProperties={genericLayoutSlotContent?.pageProperties}
                uspListContent={uspListContent}
                pageType={PageTypes.PRODUCT}
                {...(masterLayoutSlotContent?.slotContent as MasterLayoutSchema)}
              >
                {enablePDPBreadcrumbs && <Breadcrumbs />}
                <ProductScrollHandler />
                <S.Wrapper>{main}</S.Wrapper>
              </MasterLayout>
            </ExperienceOptionsProvider>
          </GalleryContextProvider>
        </LayoutDesignContextProvider>
      </PDPDynamicContextProvider>
    </PDPContextProvider>
  );
};
